/* ═══════════════════════════════════════════════════════════════════════════
   A RECEIPT, AFTERWARDS

   The paper slip's job, done properly: what was eaten, what it cost, what the
   VAT was, and enough about the place to be usable as an expense claim.

   ── Why each guest gets one, and not only the payer ───────────────────────

   Four people eat together, one taps their card, and the other three leave
   with nothing. That is how it works today and it is the wrong way round: the
   person who was ASKED for 340 kroner is the one who needs to see what the
   340 was for. So the payer gets the whole table and everybody who was asked
   for a share gets their own, showing their share and what it covers.

   It reaches whoever the platform has a number for. Nobody is made to type an
   email address at the end of a meal — see the note on delivery below.

   ── VAT ───────────────────────────────────────────────────────────────────

   Norway charges 15 % on food and 25 % on drink, and a restaurant bill is
   both. Splitting them is not a nicety: a receipt that lumps them together is
   a receipt an accountant cannot use, which is most of the reason anybody
   asks for one. The rates are stated on the receipt rather than only applied,
   so the figures can be checked.

   These are the rates in force, not a business's choice, so they live here.
   They are the one number in this file the tenant does not set.
   ═══════════════════════════════════════════════════════════════════════ */

/** Merverdiavgift, by what is being sold. */
export const VAT = { food: 0.15, drink: 0.25 };

export const VAT_WORDS = {
  food: 'Food, 15 %',
  drink: 'Drink and alcohol, 25 %',
};

const round = n => Math.round(n);

/**
 * The VAT inside a set of lines.
 *
 * Norwegian prices are quoted including VAT, so this works BACKWARDS out of
 * the total rather than adding anything on. Computing it forwards would show
 * a customer a bill larger than the one they agreed to.
 */
export function vatOf(lines = []) {
  const out = { food: { gross: 0, vat: 0 }, drink: { gross: 0, vat: 0 } };
  lines.forEach(l => {
    const bucket = l.kind === 'drink' ? 'drink' : 'food';
    const gross = l.price * (l.qty || 1);
    out[bucket].gross += gross;
    out[bucket].vat += gross - gross / (1 + VAT[bucket]);
  });
  ['food', 'drink'].forEach(k => {
    out[k].gross = round(out[k].gross);
    out[k].vat = round(out[k].vat);
    out[k].net = out[k].gross - out[k].vat;
  });
  out.gross = out.food.gross + out.drink.gross;
  out.vat = out.food.vat + out.drink.vat;
  out.net = out.gross - out.vat;
  return out;
}

/**
 * One receipt.
 *
 * `share` is what THIS person owes, which is not the table's total when the
 * bill was divided. Both are on the receipt: a person asked for 340 of an
 * 1180 bill needs to see both figures or the number looks invented.
 *
 * @param {object} p
 * @param {object} p.business
 * @param {number} p.table
 * @param {object[]} p.lines   the table's lines: { name, price, qty, kind }
 * @param {number} p.share     what this person owes
 * @param {string} p.mode      how it was divided
 * @param {string} p.name      who this receipt is for
 * @param {boolean} p.payer    whether they paid the table
 */
export function receipt({ business, table, lines = [], share, total, mode, name, payer, at }) {
  const tax = vatOf(lines);
  const whole = total ?? tax.gross;
  const owed = share ?? whole;
  return {
    /* The place, in the shape an expense claim wants: a name and a number
       that can be looked up. Never invented — see domain/signup.js; where a
       business has not given one, the line says so rather than showing a
       string of digits that is not a registration. */
    place: {
      name: business?.name || '',
      org_no: business?.org_no || '',
      street: [business?.street, business?.city].filter(Boolean).join(', '),
    },
    table,
    at: at || new Date().toISOString(),
    for: name || 'Guest',
    payer: !!payer,
    lines: lines.map(l => ({
      name: l.name,
      qty: l.qty || 1,
      price: l.price,
      sum: l.price * (l.qty || 1),
      kind: l.kind === 'drink' ? 'drink' : 'food',
    })),
    vat: tax,
    total: whole,
    share: owed,
    /* Whether this person is looking at the whole bill or a slice of it. A
       receipt that showed only the slice would be one nobody could reconcile
       against the table; one that showed only the whole would be one nobody
       could claim. */
    partial: owed !== whole,
    mode,
  };
}

/** The one line that says what this receipt is. */
export function summary(r) {
  const kr = n => Number(n || 0).toLocaleString('nb-NO');
  if (!r.partial) return `${kr(r.total)} kr · table ${r.table}`;
  return `${kr(r.share)} kr of ${kr(r.total)} kr · table ${r.table}`;
}

/**
 * How it actually reaches somebody, and how it does not.
 *
 * It lands on their Avtale.net page under the number they are known by. There
 * is no email in this build — no mail server — and asking a guest to type an
 * address at the end of a meal is how you turn a receipt into a queue at the
 * door. When there is a mail server, this is the place that grows a second
 * line and nothing else changes.
 */
export const deliveryWords = known =>
  (known
    ? 'It is on your page at Avtale.net, under your number.'
    : 'Sign in to Avtale.net with the number you used and it is there. '
      + 'Nothing was emailed — there is no mail server in this build.');
